import { inferSampleSchema, schemaFields } from './schema';
import { applyMappings, runCase, validate } from './transform';
import { CanonicalSchema, FieldMapping, Sample, TestCaseResult, TestViolation } from './types';
import { GeneratedXslt, generateXslt } from './xslt';

/**
 * A transformation that no vendor owns.
 *
 * Casa's data plane serves one shape of traffic — `/ingest/{tenant}/{vendor}/{group}/{route}` —
 * so a mapping written for anything else (a NiFi sync, a batch job, a one-off migration) cannot
 * be published to it. What is reusable is everything before that: a source example, a target
 * contract, the mapping between them, a real run, and the stylesheet that comes out. This is
 * that part on its own, and the stylesheet is the deliverable the other system executes.
 */
export interface TransformJob {
  id: string;
  name: string;
  /** What will run the stylesheet. Recorded for humans: Casa executes none of them. */
  system: string;
  /** An example of what arrives, which is also where the source field list comes from. */
  sourceSample: string;
  /** A canonical schema from the shared library, when the target is one Casa already defines. */
  targetSchemaId?: string;
  /** An example of what the target expects, for a target the library does not define. */
  targetSample?: string;
  mappings: FieldMapping[];
  notes?: string;
  updatedAt: string;
}

export interface TransformState {
  transforms?: TransformJob[];
}

/** Minimal state this module reads, so it does not depend on the vendor-side store. */
interface SchemaLibrary extends TransformState {
  schemas: CanonicalSchema[];
}

const parsed = (text?: string): unknown => {
  if (!text?.trim()) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const slug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const findTransform = (state: TransformState, id: string): TransformJob | undefined =>
  state.transforms?.find((transform) => transform.id === id);

/** The fields an example document offers, used for both sides of the mapping. */
export const exampleFields = (text?: string): CanonicalSchema['fields'] => {
  const document = parsed(text);

  return document === undefined ? [] : schemaFields(inferSampleSchema([document]));
};

/**
 * What the mapping targets: a canonical schema when one is chosen, otherwise a schema inferred
 * from the target example. Everything downstream — validation, XSLT — reads only this.
 */
export const targetSchemaOf = (state: SchemaLibrary, transform: TransformJob): CanonicalSchema | undefined => {
  if (transform.targetSchemaId) return state.schemas.find((schema) => schema.id === transform.targetSchemaId);

  const fields = exampleFields(transform.targetSample);
  if (fields.length === 0) return undefined;

  return {
    id: `${transform.id}-target`,
    code: slug(transform.name) || transform.id,
    version: 1,
    title: `${transform.name} target`,
    // inferred from an example, so nothing is required: the example proves shape, not obligation
    fields: fields.map((field) => ({ ...field, required: false })),
  };
};

/** The mapping run against its own source example. No delivery, and nothing is recorded. */
export const transformPreview = (state: SchemaLibrary, transform: TransformJob): TestCaseResult | undefined => {
  if (parsed(transform.sourceSample) === undefined) return undefined;
  const sample: Sample = {
    id: `${transform.id}-example`,
    routeId: transform.id,
    scope: 'shared',
    name: 'Source example',
    payload: transform.sourceSample,
  };

  return runCase(sample, transform.mappings, targetSchemaOf(state, transform));
};

export const transformViolations = (state: SchemaLibrary, transform: TransformJob): TestViolation[] => {
  const document = parsed(transform.sourceSample);
  if (document === undefined) return [];

  return validate(applyMappings(document, transform.mappings), targetSchemaOf(state, transform));
};

/** The stylesheet the other system runs. This, not a Casa publication, is the deliverable. */
export const transformXslt = (state: SchemaLibrary, transform: TransformJob): GeneratedXslt =>
  generateXslt(transform.mappings, targetSchemaOf(state, transform));

export const addTransform = <T extends TransformState>(state: T, name: string, system: string): T => {
  const id = slug(name);
  if (!id || state.transforms?.some((transform) => transform.id === id)) return state;

  return {
    ...state,
    transforms: [
      ...(state.transforms ?? []),
      {
        id,
        name: name.trim(),
        system: system.trim() || 'Unspecified',
        sourceSample: '',
        mappings: [],
        updatedAt: new Date().toISOString(),
      },
    ],
  };
};

export const saveTransform = <T extends TransformState>(state: T, id: string, patch: Partial<TransformJob>): T => {
  if (!findTransform(state, id)) return state;

  return {
    ...state,
    transforms: (state.transforms ?? []).map((transform) =>
      transform.id === id ? { ...transform, ...patch, id, updatedAt: new Date().toISOString() } : transform,
    ),
  };
};

export const removeTransform = <T extends TransformState>(state: T, id: string): T => ({
  ...state,
  transforms: (state.transforms ?? []).filter((transform) => transform.id !== id),
});
